type Polygon = number[][][]

export type LandmassInfo = {
  id: string
  name: string
  type: 'continent' | 'island'
  continentGroup: LandmassContinentGroup
  areaGroup: LandmassAreaGroup
  centroid: [number, number]
  area: number
  polygonIndices: number[]
}

export const LANDMASS_CONTINENT_GROUPS = [
  'North America',
  'South America',
  'Europe',
  'Africa',
  'Asia',
  'Oceania',
  'Antarctica',
  'Other',
] as const

export type LandmassContinentGroup = typeof LANDMASS_CONTINENT_GROUPS[number]

export const LANDMASS_AREA_GROUPS = [
  'continent',
  'large-island',
  'medium-island',
  'small-island',
] as const

export type LandmassAreaGroup = typeof LANDMASS_AREA_GROUPS[number]

const computeCentroid = (rings: number[][][]): [number, number] => {
  let sumLat = 0, sumLng = 0, count = 0
  for (const coord of rings[0]) {
    sumLng += coord[0]
    sumLat += coord[1]
    count++
  }
  return [sumLat / count, sumLng / count]
}

const computeArea = (rings: number[][][]): number => {
  const ring = rings[0]
  if (ring.length < 3) return 0
  let area = 0
  for (let i = 0; i < ring.length; i++) {
    const j = (i + 1) % ring.length
    area += ring[i][0] * ring[j][1]
    area -= ring[j][0] * ring[i][1]
  }
  return Math.abs(area) / 2
}

type RefLandmass = {
  name: string
  lat: number
  lng: number
  radius: number
  type: 'continent' | 'island'
}

const REFERENCE_LANDMASSES: RefLandmass[] = [
  // Continents
  { name: "Eurasia", lat: 50, lng: 70, radius: 40, type: 'continent' },
  { name: "Africa", lat: 2, lng: 22, radius: 30, type: 'continent' },
  { name: "North America", lat: 48, lng: -100, radius: 25, type: 'continent' },
  { name: "South America", lat: -14, lng: -56, radius: 25, type: 'continent' },
  { name: "Australia", lat: -25, lng: 134, radius: 15, type: 'continent' },
  { name: "Antarctica", lat: -82, lng: 0, radius: 20, type: 'continent' },
  // Major islands
  { name: "Greenland", lat: 72, lng: -40, radius: 10, type: 'island' },
  { name: "New Guinea", lat: -5, lng: 141, radius: 8, type: 'island' },
  { name: "Borneo", lat: 1, lng: 114, radius: 8, type: 'island' },
  { name: "Madagascar", lat: -19, lng: 47, radius: 8, type: 'island' },
  { name: "Baffin Island", lat: 68, lng: -72, radius: 6, type: 'island' },
  { name: "Sumatra", lat: -1, lng: 102, radius: 6, type: 'island' },
  { name: "Honshu", lat: 36, lng: 138, radius: 5, type: 'island' },
  { name: "Great Britain", lat: 54, lng: -2, radius: 5, type: 'island' },
  { name: "Sulawesi", lat: -2, lng: 121, radius: 5, type: 'island' },
  { name: "Ellesmere Island", lat: 80, lng: -78, radius: 5, type: 'island' },
  { name: "Victoria Island", lat: 71, lng: -110, radius: 5, type: 'island' },
  { name: "Banks Island", lat: 73, lng: -122, radius: 4, type: 'island' },
  { name: "Devon Island", lat: 75, lng: -86, radius: 4, type: 'island' },
  { name: "Axel Heiberg Island", lat: 79, lng: -91, radius: 4, type: 'island' },
  { name: "Melville Island", lat: 76, lng: -112, radius: 4, type: 'island' },
  { name: "Southampton Island", lat: 64, lng: -85, radius: 4, type: 'island' },
  { name: "New Zealand South Island", lat: -44, lng: 170, radius: 4, type: 'island' },
  { name: "New Zealand North Island", lat: -38, lng: 176, radius: 4, type: 'island' },
  { name: "Java", lat: -7, lng: 110, radius: 4, type: 'island' },
  { name: "Cuba", lat: 22, lng: -79, radius: 4, type: 'island' },
  { name: "Luzon", lat: 16, lng: 121, radius: 4, type: 'island' },
  { name: "Mindanao", lat: 8, lng: 126, radius: 4, type: 'island' },
  { name: "Iceland", lat: 65, lng: -18, radius: 4, type: 'island' },
  { name: "Ireland", lat: 53, lng: -8, radius: 4, type: 'island' },
  { name: "Hokkaido", lat: 43, lng: 143, radius: 4, type: 'island' },
  { name: "Hispaniola", lat: 19, lng: -71, radius: 4, type: 'island' },
  { name: "Sakhalin", lat: 51, lng: 143, radius: 4, type: 'island' },
  { name: "Sri Lanka", lat: 8, lng: 81, radius: 4, type: 'island' },
  { name: "Tasmania", lat: -42, lng: 146, radius: 4, type: 'island' },
  { name: "Svalbard", lat: 78, lng: 18, radius: 4, type: 'island' },
  { name: "Novaya Zemlya", lat: 74, lng: 56, radius: 5, type: 'island' },
  { name: "Taiwan", lat: 24, lng: 121, radius: 3, type: 'island' },
  { name: "Kyushu", lat: 33, lng: 131, radius: 3, type: 'island' },
  { name: "Tierra del Fuego", lat: -54, lng: -69, radius: 4, type: 'island' },
  { name: "Prince of Wales Island", lat: 73, lng: -99, radius: 3, type: 'island' },
  { name: "Timor", lat: -9, lng: 126, radius: 3, type: 'island' },
  { name: "Sicily", lat: 37, lng: 14, radius: 3, type: 'island' },
  { name: "Sardinia", lat: 40, lng: 9, radius: 3, type: 'island' },
  { name: "Falkland Islands", lat: -52, lng: -59, radius: 3, type: 'island' },
  { name: "Kamchatka Peninsula", lat: 57, lng: 160, radius: 5, type: 'island' },
  { name: "Novaya Zemlya North", lat: 76, lng: 60, radius: 3, type: 'island' },
]

const haversineDistance = (
  lat1: number, lng1: number, lat2: number, lng2: number
): number => {
  const dlat = lat2 - lat1
  const dlng = lng2 - lng1
  return Math.sqrt(dlat * dlat + dlng * dlng)
}

const findNearestRef = (
  lat: number, lng: number
): RefLandmass | null => {
  let best: RefLandmass | null = null
  let bestDist = Infinity
  for (const ref of REFERENCE_LANDMASSES) {
    const dist = haversineDistance(lat, lng, ref.lat, ref.lng)
    if (dist < ref.radius && dist < bestDist) {
      bestDist = dist
      best = ref
    }
  }
  return best
}

const regionFromCentroid = (lat: number, lng: number): string => {
  if (lat < -60) return "Antarctic"
  if (lat > 55 && lng > -140 && lng < -50) return "Canadian Arctic"
  if (lat > 60 && lng > -50 && lng < -10) return "North Atlantic"
  if (lat > 55 && lng > -10 && lng < 50) return "Northern Europe"
  if (lat > 55 && lng >= 50 && lng < 180) return "Northern Asia"
  if (lat > 0 && lng > -170 && lng < -30) return "North America"
  if (lat <= 0 && lng > -90 && lng < -30) return "South America"
  if (lat > 0 && lat <= 55 && lng > -30 && lng < 50) return "Europe/Mediterranean"
  if (lat > -40 && lat <= 0 && lng > -30 && lng < 55) return "Africa"
  if (lat > 0 && lat <= 55 && lng >= 50 && lng < 100) return "Central Asia"
  if (lat > 0 && lat <= 55 && lng >= 100) return "East Asia"
  if (lng >= 95 && lng <= 155 && lat <= 0 && lat > -15) return "Southeast Asia"
  if (lng >= 100 && lat <= -15) return "Oceania"
  return "Other"
}

const continentGroupFromRegion = (
  region: string
): LandmassContinentGroup => {
  if (region === "North America" || region === "Canadian Arctic") {
    return "North America"
  }
  if (region === "South America") return "South America"
  if (
    region === "North Atlantic" ||
    region === "Northern Europe" ||
    region === "Europe/Mediterranean"
  ) {
    return "Europe"
  }
  if (region === "Africa") return "Africa"
  if (
    region === "Northern Asia" ||
    region === "Central Asia" ||
    region === "East Asia" ||
    region === "Southeast Asia"
  ) {
    return "Asia"
  }
  if (region === "Oceania") return "Oceania"
  if (region === "Antarctic") return "Antarctica"
  return "Other"
}

const continentGroupFromCentroid = (
  lat: number, lng: number
): LandmassContinentGroup => {
  return continentGroupFromRegion(regionFromCentroid(lat, lng))
}

const areaGroupFromLandmass = (
  type: 'continent' | 'island', area: number
): LandmassAreaGroup => {
  if (type === 'continent') return 'continent'
  if (area >= 50) return 'large-island'
  if (area >= 10) return 'medium-island'
  return 'small-island'
}

const CONTINENT_AREA_THRESHOLD = 3000

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const extractPolygonsFromGeoJson = (
  geoJson: { type: string; geometry?: any; features?: any[] }
): Polygon[] => {
  const polygons: Polygon[] = []
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const extract = (geometry: any) => {
    if (geometry.type === "Polygon") {
      polygons.push(geometry.coordinates)
    } else if (geometry.type === "MultiPolygon") {
      for (const poly of geometry.coordinates) {
        polygons.push(poly)
      }
    }
  }
  if (geoJson.features) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    for (const feat of geoJson.features) extract((feat as any).geometry)
  } else if (geoJson.geometry) {
    extract(geoJson.geometry)
  }
  return polygons
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const classifyLandmasses = (
  geoJson: { type: string; geometry?: any; features?: any[] }
): LandmassInfo[] => {
  const polygons = extractPolygonsFromGeoJson(geoJson)
  const rawEntries = polygons.map((poly, i) => ({
    index: i,
    centroid: computeCentroid(poly),
    area: computeArea(poly),
  }))
  const usedNames = new Set<string>()
  const regionCounters: Record<string, number> = {}
  const sortedByArea = [...rawEntries].sort((a, b) => b.area - a.area)
  const landmasses: LandmassInfo[] = sortedByArea.map((entry) => {
    const [lat, lng] = entry.centroid
    const continentGroup = continentGroupFromCentroid(lat, lng)
    const ref = findNearestRef(lat, lng)
    let name: string
    let type: 'continent' | 'island'
    if (ref && !usedNames.has(ref.name)) {
      name = ref.name
      type = ref.type
      usedNames.add(name)
    } else if (entry.area > CONTINENT_AREA_THRESHOLD) {
      const region = regionFromCentroid(lat, lng)
      name = `${region} (large)`
      type = 'continent'
    } else {
      const region = regionFromCentroid(lat, lng)
      regionCounters[region] = (regionCounters[region] || 0) + 1
      const count = regionCounters[region]
      name = `${region} island ${count}`
      type = 'island'
    }
    return {
      id: `landmass-${entry.index}`,
      name,
      type,
      continentGroup,
      areaGroup: areaGroupFromLandmass(type, entry.area),
      centroid: entry.centroid,
      area: entry.area,
      polygonIndices: [entry.index],
    }
  })
  landmasses.sort((a, b) => {
    if (a.type !== b.type) return a.type === 'continent' ? -1 : 1
    return b.area - a.area
  })
  return landmasses
}
